import React, { useEffect, useState, useMemo } from 'react';
import api from '../api/client';
import { BarChart3, Loader2, MapPin, Trophy, Filter, Building2 } from 'lucide-react';
import SearchBar, { matches } from '../components/app/SearchBar';

const OPS = [
  { key: '', label: 'Todos' },
  { key: 'ORANGE', label: 'ORANGE', c: '#ff6b00' },
  { key: 'WILLIAM_VDF', label: 'VDF', c: '#e60000' },
  { key: 'WILLIAM_OSP', label: 'OSP', c: '#3b82f6' },
  { key: 'AVE_OSP', label: 'AVE', c: '#10b981' },
];

const Ranking = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [op, setOp] = useState('');
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/coverage/ranking', { params: { limit: 50, operator: op || undefined } });
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [op]); // eslint-disable-line

  const max = Math.max(1, ...items.map((i) => i.score || 0));
  const filtered = useMemo(() => items.filter((it) => matches(search, it.municipality, it.province)), [items, search]);

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto" data-testid="ranking-page">
      <div className="mb-6">
        <div className="text-xs font-bold tracking-widest text-[#A3FF12]">SEGUIMIENTO</div>
        <h1 className="text-3xl sm:text-4xl font-black mt-1">Ranking de cobertura por municipio</h1>
        <p className="text-white/50 text-sm mt-1">Los 50 municipios donde Adquiriam tiene más presencia (1,5 × nodos + torres). Ideal para priorizar ofrecimientos.</p>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Filter size={14} className="text-white/40"/>
        {OPS.map((o) => (
          <button key={o.key || 'all'} onClick={() => setOp(o.key)} data-testid={`ranking-op-${o.key || 'all'}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
              op === o.key ? 'bg-[#A3FF12]/10 text-[#A3FF12] border-[#A3FF12]/40' : 'bg-white/5 text-white/60 border-white/10 hover:text-white'
            }`}>
            {o.c && <span style={{ background: o.c, width: 8, height: 8, borderRadius: '50%' }}/>}
            {o.label}
          </button>
        ))}
        <div className="flex-1 min-w-[200px] max-w-md">
          <SearchBar value={search} onChange={setSearch} placeholder="Buscar municipio o provincia…" testId="ranking-search"/>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-[#A3FF12]" size={32}/></div>
      ) : items.length === 0 ? (
        <div className="text-center py-24 border border-dashed border-white/10 rounded-2xl">
          <BarChart3 className="mx-auto text-white/20 mb-2" size={40}/>
          <p className="text-white/50">Sin datos.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.length === 0 && (
            <div className="text-center py-10 text-white/40 text-sm">Ningún municipio coincide con «{search}».</div>
          )}
          {filtered.map((it, idx) => {
            const wPct = ((it.score || 0) / max) * 100;
            return (
              <div key={`${it.municipality}-${idx}`} data-testid={`ranking-row-${idx}`}
                className="bg-black/30 border border-white/10 hover:border-[#A3FF12]/30 rounded-xl p-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 text-center font-black text-xl ${idx < 3 ? 'text-[#A3FF12]' : 'text-white/40'}`}>
                    {idx < 3 ? <Trophy size={20} className="mx-auto"/> : `#${idx + 1}`}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <MapPin size={12} className="text-white/50"/>
                      <div className="text-white font-bold truncate">{it.municipality || 'N/D'}</div>
                      {it.province && <span className="text-white/40 text-xs">· {it.province}</span>}
                    </div>
                    <div className="mt-1 relative h-2 bg-black/50 rounded overflow-hidden">
                      <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#A3FF12] to-[#A3FF12]/40 rounded" style={{ width: `${wPct}%` }}/>
                    </div>
                    <div className="flex items-center gap-3 mt-1.5 text-[11px] text-white/60 flex-wrap">
                      <span><b className="text-white">{it.nodes || 0}</b> nodos</span>
                      <span><b className="text-purple-300">{it.towers || 0}</b> torres</span>
                      {typeof it.avg_tower_height_m === 'number' && <span>Altura media: <b>{it.avg_tower_height_m} m</b></span>}
                      {it.operator_diversity > 0 && <span>{it.operator_diversity} operador(es)</span>}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-2xl font-black text-[#A3FF12]">{Math.round(it.score || 0)}</div>
                    <div className="text-[10px] uppercase text-white/40">score</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-6 text-center text-white/40 text-xs flex items-center justify-center gap-2">
        <Building2 size={12}/> Score = 1,5 × nodos ON-AIR + torres físicas. Cuanta más presencia, más margen para ofrecimientos cruzados.
      </div>
    </div>
  );
};

export default Ranking;
